import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OrderOut } from '@/types/api';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePriceFormat } from '@/hooks/usePriceFormat';
// import { QRCodeUtils } from '@/utils/qrCodeUtils';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import Icon from '@/components/atoms/Icon/Icon';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import './UserOrders.scss';

const UserOrders: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { formatPrice } = usePriceFormat();
  const { formatOrderDate } = useDateFormatter();
  const [orders, setOrders] = useState<OrderOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'confirmed':
      case 'preparing':
        return 'info';
      case 'ready':
      case 'completed':
      case 'paid':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleViewOrder = (orderId: number) => {
    navigate(`/order-confirmation/${orderId}`);
  };

  useEffect(() => {
    const fetchUserOrders = async () => {
      if (!isAuthenticated) {
        setError(t('userOrders.notAuthenticated'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userOrders = await apiService.getOrders();
        setOrders(userOrders);
      } catch (err: any) {
        setError(t('userOrders.errorFetching'));
      } finally {
        setLoading(false);
      }
    };

    fetchUserOrders();
  }, [isAuthenticated, t]);

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="user-orders-page">
          <Card variant="elevated" className="error-card">
            <div className="error-card__content">
              <h2 className="error-card__title">{t('common.error')}</h2>
              <p className="error-card__message">{error}</p>
              <Button 
                variant="primary" 
                onClick={() => navigate('/events')}
              >
                {t('userOrders.backToEvents')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="container">
        <div className="user-orders-page">
          <div className="user-orders-page__header">
            <h1 className="user-orders-page__title">
              {t('userOrders.title')}
            </h1>
          </div>

          <Card variant="outlined" className="empty-orders-card">
            <div className="empty-orders-card__content">
              <div className="empty-orders-card__icon">
                <Icon name="pedidos" size={20} alt={t('userOrders.empty')} />
              </div>
              <h2 className="empty-orders-card__title">
                {t('userOrders.empty')}
              </h2>
              <p className="empty-orders-card__description">
                {t('userOrders.emptyDescription')}
              </p>
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => navigate('/events')}
              >
                {t('userOrders.browseEvents')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="user-orders-page">
        <div className="user-orders-page__header">
          <h1 className="user-orders-page__title">
            {t('userOrders.title')}
          </h1>
          <p className="user-orders-page__subtitle">
            {t('userOrders.subtitle', { count: orders.length })}
          </p>
        </div>

        <div className="user-orders-page__content">
          {orders.map((order) => (
            <Card key={order.id} variant="elevated" className="order-card">
              <div className="order-card__header">
                <div className="order-card__info">
                  <h3 className="order-card__order-number">
                    {t('userOrders.orderNumber')} #{order.id}
                  </h3>
                  <p className="order-card__date">
                    {formatOrderDate(order.created_at)}
                  </p>
                </div>
                <div className="order-card__status">
                  <span className={`status-badge status-badge--${getStatusColor(order.status)}`}>
                    {t(`orderConfirmation.statuses.${order.status.toLowerCase()}`)}
                  </span>
                </div>
              </div>

              <div className="order-card__items">
                
                <div className="order-card__items-list">
                  {order.items.slice(0, 3).map((item, index) => (
                    <div key={index} className="order-card__item">
                      <span className="order-card__item-name">
                        {item.drink_name || item.food_name || t('userOrders.unknownItem')}
                      </span>
                      <span className="order-card__item-quantity">
                        x{item.quantity}
                      </span>
                      <span className="order-card__item-price">
                        {formatPrice(item.price_breakdown.total)}
                      </span>
                    </div>
                  ))}
                  {order.items.length > 3 && (
                    <div className="order-card__more-items">
                      {t('userOrders.andMore', { count: order.items.length - 3 })}
                    </div>
                  )}
                </div>
              </div>

              <div className="order-card__footer">
                <div className="order-card__total">
                  <span className="order-card__total-label">
                    {t('userOrders.total')}:
                  </span>
                  <span className="order-card__total-amount">
                    {formatPrice(order.total_amount)}
                  </span>
                </div>
                <div className="order-card__actions">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewOrder(order.id)}
                  >
                    {t('userOrders.viewDetails')}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserOrders;
